import { z } from 'zod';

import { isDefined } from '../../../utils.js';
import { conditionalConfigSchema } from '../utils/conditionalConfig.js';

export * from './lookbackDuration.js';

export enum FlowKind {
  Base = 'Base',
  Skill = 'Skill',
  FlowTemplate = 'FlowTemplate',
}

export enum BlockType {
  Conversation = 'Conversation',
  CreateEncounterSummary = 'CreateEncounterSummary',
  CreatePatientOrder = 'CreatePatientOrder',
  Start = 'Start',
  SubFlowV1 = 'SubFlow',
  SubFlowV2 = 'SubFlowV2',
  ScriptedFlow = 'ScriptedFlow',
  Switch = 'Switch',
  Wait = 'Wait',
  WaitV2 = 'WaitV2',
  LlmPrompt = 'LlmPrompt',
  Rest = 'Rest',
  DocumentExtract = 'DocumentExtract',
  DocumentTemplate = 'DocumentTemplate',
  GenerateFile = 'GenerateFile',
  AdvancedFhir = 'AdvancedFhir',
  Fhir = 'Fhir',
  WaitForSignal = 'WaitForSignal',
  WaitForFormCompletion = 'WaitForFormCompletion',
  Form = 'Form',
  FormV2 = 'FormV2',
  GetExternalDocumentPacket = 'GetExternalDocumentPacket',
  GetFormSet = 'GetFormSet',
  GetInsurances = 'GetInsurances',
  GetPatient = 'GetPatient',
  GetPatientOrder = 'GetPatientOrder',
  GetNotableObject = 'GetNotableObject',
  GetProvider = 'GetProvider',
  Ocr = 'Ocr',
  OcrV2 = 'OcrV2',
  Output = 'Output',
  CareGapAlgorithmBlock = 'CareGapAlgorithmBlock',
  AsynchronousExtractJob = 'AsynchronousExtractJob',
  AsynchronousExportJob = 'AsynchronousExportJob',
  HCCAlgorithmBlock = 'HCCAlgorithmBlock',
  ExportTextToBucket = 'ExportTextToBucket',
  ExportExternalDocumentPacket = 'ExportExternalDocumentPacket',
  Scheduling = 'Scheduling',
  VoiceCall = 'VoiceCall',
  VoiceCallV2 = 'VoiceCallV2',
  InsuranceCard = 'InsuranceCard',
  Callback = 'Callback',
  Evaluation = 'Evaluation',
  VoiceTranscribe = 'VoiceTranscribe',
  SendEmail = 'SendEmail',
  SendSms = 'SendSms',
  MedicalNecessityCheck = 'MedicalNecessityCheck',
  PatientDataStore = 'PatientDataStore',
  FilterList = 'FilterList',
  AuthorizationSubmission = 'AuthorizationSubmission',
  HumanTask = 'HumanTask',
  Fax = 'Fax',
  InformationExtraction = 'InformationExtraction',
  UpdateFlowConfig = 'UpdateFlowConfig',
  Hl7 = 'HL7',
  IteratorV1 = 'Iterator',
  IteratorV2 = 'IteratorV2',
  Repeat = 'Repeat',
  HieQueryDocuments = 'HieQueryDocuments',
  HieGetDocuments = 'HieGetDocuments',
  DocumentSearch = 'DocumentSearch',
  RestApiClassicAuthorization = 'RestApiClassicAuthorization',
  GetSftpFile = 'GetSftpFile',
  UpdateNotableObject = 'UpdateNotableObject',
  CreateNotableObject = 'CreateNotableObject',
  RealTimeEligibility = 'RealTimeEligibility',
  FileConversion = 'FileConversion',
  Mirth = 'Mirth',
  Text = 'Text',
  ServiceReview = 'ServiceReview',
  Conditional = 'Conditional',
  MontageOnBase = 'MontageOnBase',
}

export enum ObsoleteBlockType {
  ObsoleteHumanReview = 'HumanReview',
  ObsoleteApiSubmissions = 'ApiSubmissions',
  ObsoleteGetEncounterSummary = 'GetEncounterSummary',
  ObsoleteSkill = 'Skill',
  ObsoleteSendToLabelboxForReview = 'SendToLabelboxForReview',
  ObsoleteWaitForAvailableSchedulingSlot = 'WaitForAvailableSchedulingSlot',
}

export enum UnknownBlockType {
  /**
   * This is the only unknown block type allowed.
   * It handles cases where the API says there is a block, but the type is not known.
   * This can happen during upgrades or when the API is out of sync.
   */
  Unknown = 'Unknown',
}

export enum SpecialBlockType {
  /**
   * Skills are special since they cannot be picked directly as a block.
   * Instead, they are hydrated based on the skill API data, when picked.
   * The block can still be copied and pasted, with that specific skill data.
   */
  SkillV2 = 'SkillV2',
}

export const SingletonBlockTypes = new Set<BlockType>([BlockType.Start, BlockType.Output]);

export const AnyBlockTypeSchema = z
  .nativeEnum(BlockType)
  .or(z.nativeEnum(ObsoleteBlockType))
  .or(z.nativeEnum(UnknownBlockType))
  .or(z.nativeEnum(SpecialBlockType));
export type AnyBlockType = z.infer<typeof AnyBlockTypeSchema>;

export enum ProducedType {
  /** @deprecated
   * legacy Labelbox enums. We no longer support Labelbox, but if any runs have these types, the schema should still validate.
   */
  ObsoleteLabelboxAnnotations = 'LabelboxAnnotations',
  /** @deprecated
   * legacy Labelbox enums. We no longer support Labelbox, but if any runs have these types, the schema should still validate.
   */
  ObsoleteLabelboxReviewResult = 'LabelboxReviewResult',
  AgentConversation = 'AgentConversation',
  Any = 'Any',
  Appointments = 'Appointments',
  Conditions = 'Conditions',
  Coverages = 'Coverages',
  DocumentBinaries = 'DocumentBinaries',
  DocumentExtract = 'DocumentExtract',
  DocumentMetadata = 'DocumentMetadata',
  DocumentSearch = 'DocumentSearch',
  Encounter = 'Encounter',
  EncounterSummary = 'EncounterSummary',
  Encounters = 'Encounters',
  Evaluation = 'Evaluation',
  ExternalDocumentPacket = 'ExternalDocumentPacket',
  FhirPatientResponse = 'FhirPatientResponse',
  FileConversion = 'FileConversion',
  FileReference = 'FileReference',
  HccSkill = 'HccSkill',
  HumanTask = 'HumanTask',
  InformationExtraction = 'InformationExtraction',
  InsuranceCard = 'InsuranceCard',
  Insurances = 'Insurances',
  LabValues = 'LabValues',
  ListOfObjects = 'ListOfObjects',
  Medications = 'Medications',
  OcrDetailResponse = 'OcrDetailResponse',
  Patient = 'Patient',
  PatientFindings = 'PatientFindings',
  PatientOrder = 'PatientOrder',
  PdsBookedAppointments = 'PdsBookedAppointments',
  PdsConditions = 'PdsConditions',
  PdsCoverages = 'PdsCoverages',
  PdsDocumentBinaries = 'PdsDocumentBinaries',
  PdsDocumentMetadata = 'PdsDocumentMetadata',
  PdsDocumentSearchResults = 'PdsDocumentSearchResults',
  PdsDocumentTranscriptions = 'PdsDocumentTranscriptions',
  PdsEncounters = 'PdsEncounters',
  PdsLabValues = 'PdsLabValues',
  PdsMedications = 'PdsMedications',
  PdsOrders = 'PdsOrders',
  PdsPractitioners = 'PdsPractitioners',
  PdsSurgicalHistories = 'PdsSurgicalHistories',
  Practitioner = 'Practitioner',
  Procedures = 'Procedures',
  Provider = 'Provider',
  ServiceRequests = 'ServiceRequests',
  SendEmail = 'SendEmail',
  Start = 'Start',
  SubFlowV1Result = 'SubflowResult',
  SurgicalHistories = 'SurgicalHistories',
  UpdateFlowConfigData = 'UpdateFlowConfigData',
  VoiceCall = 'VoiceCall',
  VoiceCallV2 = 'VoiceCallV2',
  VoiceTranscribe = 'VoiceTranscribe',
}

export enum NotableObject {
  AgentConversation = 'AgentConversation',
  Assistant = 'Assistant',
  AuthorizationSubmissions = 'AuthorizationSubmissions',
  Payment = 'Payment',
  ReferenceTable = 'ReferenceTable',
  Hl7MessageEvent = 'Hl7MessageEvent',
  Guarantor = 'Guarantor',
  PatientOrder = 'PatientOrder',
  Template = 'Template',
  Provider = 'Provider',
  PatientInsuranceInformation = 'PatientInsuranceInformation',
  ExternalDocumentPacket = 'ExternalDocumentPacket',
  Patient = 'Patient',
  Practice = 'Practice',
  SftpMessageEvent = 'SftpMessageEvent',
  X12MessageEvent = 'X12MessageEvent',
  PatientUpload = 'PatientUpload',
  Encounter = 'Encounter',
  PrimaryCareProvider = 'PrimaryCareProvider',
  FeedMessages = 'FeedMessages',
  EncounterContacts = 'EncounterContacts',
  InsuranceOcrResults = 'InsuranceOcrResults',
  ServiceReviewSubmissions = 'ServiceReviewSubmissions',
  JobContext = 'JobContext',
}

export enum RunMode {
  Live = 'LIVE',
  Test = 'TEST',
}

export function returnTypeSchema<T extends ProducedType>(returnType: T) {
  return z
    .any()
    .optional()
    .transform((): T => returnType);
}

export const BaseFlowBlockUiDataSchema = z.object({
  position: z.object({
    x: z.number(),
    y: z.number(),
  }),
  groupNodeId: z.string().optional(),
  areExtraTargetHandlesVisible: z.boolean().optional(),
});
export type BaseFlowBlockUiData = z.infer<typeof BaseFlowBlockUiDataSchema>;

export enum FlowBlockEventTrigger {
  OnBlockCompleted = 'OnBlockCompleted',
  OnError = 'OnBlockError',
}

export enum FlowRunAnalyticEventCategory {
  Authorizations = 'Authorizations',
  ReferralTranscription = 'ReferralTranscription',
  IntakePayments = 'IntakePayments',
  ReferralScheduling = 'ReferralScheduling',
  CareGaps = 'CareGaps',
  HccSuspecting = 'HccSuspecting',
  OutboundScheduling = 'OutboundScheduling',
  Custom = 'Custom',
}

export const BaseFlowBlockEventDefinitionSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1).optional(),
    trigger: z.nativeEnum(FlowBlockEventTrigger),
    customAttributes: z
      .array(z.object({ key: z.string().min(1), value: z.string().min(1) }))
      .optional(),
    category: z
      .nativeEnum(FlowRunAnalyticEventCategory)
      .optional()
      .default(FlowRunAnalyticEventCategory.Custom),
    standardAnalyticEventId: z.uuid().optional(),
    conditions: conditionalConfigSchema.optional(),
  })
  .refine(
    (data) => {
      return (
        (isDefined(data.name) && isDefined(data.category)) ||
        isDefined(data.standardAnalyticEventId)
      );
    },
    {
      message:
        "Either both 'name' and 'category' must be provided, or 'standardAnalyticEventId' must be provided",
    }
  );

export enum FlowRunBlockDataSource {
  Empty = 'Empty',
  Inherited = 'Inherited',
  Mocked = 'Mocked',
}

export type FlowBlockEventDefinition = z.infer<typeof BaseFlowBlockEventDefinitionSchema>;

export const BaseFlowBlockSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional().default(''),
  uiData: BaseFlowBlockUiDataSchema,
  mockDataEnabled: z.boolean().optional(),
  mockData: z.any().optional(),
  returnType: z.nativeEnum(ProducedType).default(ProducedType.Any),
  milestoneName: z.string().optional(),
  events: z.array(BaseFlowBlockEventDefinitionSchema).optional(),
  stats: z.record(z.string(), z.number()).optional(),
});

export const BaseNotableObjectConfigSchema = z.object({
  notableObject: z.nativeEnum(NotableObject),
});

/**
 * TODO: Convert FlowBlocks to schemas to allow us to remove this duplicate interface
 */
export interface BaseFlowBlock<T extends ProducedType = ProducedType.Any> {
  id: string;
  name: string;
  description?: string;
  returnType?: T;
  uiData: {
    position: { x: number; y: number };
    groupNodeId?: string;
    areExtraTargetHandlesVisible?: boolean;
  };
  milestoneName?: string;
  events?: FlowBlockEventDefinition[];
  mockDataEnabled?: boolean;
  mockData?: unknown;
  stats?: Record<string, number>;
}

// In backend ConsumerOf includes a consumesType field

export const ConsumerOfSchema = z
  .object({
    sourceBlockName: z.string(),
  })
  .required();

export type ConsumerOf = z.infer<typeof ConsumerOfSchema>;

export type ConsumerOfPatient = ConsumerOf;
export type ConsumerOfPatientOrder = ConsumerOf;
export type ConsumerOfProvider = ConsumerOf;
export type ConsumerOfFhirPatientResponse = ConsumerOf;

export type ConsumerOfExternalDocumentPacket = ConsumerOf;
export type ConsumerOfConditions = ConsumerOf;
export type ConsumerOfDocumentExtract = ConsumerOf;
export type ConsumerOfLabValues = ConsumerOf;
export type ConsumerOfMedications = ConsumerOf;
export type ConsumerOfSurgicalHistories = ConsumerOf;
export type ConsumerOfVoiceCall = ConsumerOf;

export interface JustSourceBlockName {
  sourceBlockName: string;
}

export const ConsumerOfLabValuesSchema = z
  .object({
    // Don't require consumesType in the input payload, we hardcode it
    sourceBlockName: z.string(),
  })
  .transform(({ sourceBlockName }) => ({
    sourceBlockName,
    consumesType: ProducedType.LabValues,
  })) as z.ZodType<ConsumerOfLabValues, JustSourceBlockName>;

export const ConsumerOfDocumentExtractSchema = z
  .object({
    // Don't require consumesType in the input payload, we hardcode it
    sourceBlockName: z.string(),
  })
  .transform(({ sourceBlockName }) => ({
    sourceBlockName,
    consumesType: ProducedType.DocumentExtract,
  })) as z.ZodType<ConsumerOfDocumentExtract, JustSourceBlockName>;

export const ConsumerOfConditionsSchema = z
  .object({
    // Don't require consumesType in the input payload, we hardcode it
    sourceBlockName: z.string(),
  })
  .transform(({ sourceBlockName }) => ({
    sourceBlockName,
    consumesType: ProducedType.Conditions,
  })) as z.ZodType<ConsumerOfConditions, JustSourceBlockName>;

export const ConsumerOfMedicationsSchema = z
  .object({
    // Don't require consumesType in the input payload, we hardcode it
    sourceBlockName: z.string(),
  })
  .transform(({ sourceBlockName }) => ({
    sourceBlockName,
    consumesType: ProducedType.Medications,
  })) as z.ZodType<ConsumerOfMedications, JustSourceBlockName>;

export const ConsumerOfSurgicalHistoriesSchema = z
  .object({
    // Don't require consumesType in the input payload, we hardcode it
    sourceBlockName: z.string(),
  })
  .transform(({ sourceBlockName }) => ({
    sourceBlockName,
    consumesType: ProducedType.SurgicalHistories,
  })) as z.ZodType<ConsumerOfSurgicalHistories, JustSourceBlockName>;

export const ConsumerOfExternalDocumentPacketSchema = z
  .object({
    // Don't require consumesType in the input payload, we hardcode it
    sourceBlockName: z.string(),
  })
  .transform(({ sourceBlockName }) => ({
    sourceBlockName,
    consumesType: ProducedType.ExternalDocumentPacket,
  })) as z.ZodType<ConsumerOfExternalDocumentPacket, JustSourceBlockName>;

export function ConsumerOfPatientSchema(props: { isRequired: boolean }) {
  const { isRequired } = props;
  return z
    .object(
      {
        // Don't require consumesType in the input payload, we hardcode it
        sourceBlockName: z.string().min(isRequired ? 1 : 0, {
          message: 'Source block name is required',
        }),
      },
      { message: 'Patient is required' }
    )
    .optional()
    .default({
      sourceBlockName: '',
    })
    .transform(({ sourceBlockName }) => ({
      sourceBlockName,
      consumesType: ProducedType.Patient,
    })) as z.ZodType<ConsumerOfPatient, JustSourceBlockName>;
}

export function ConsumerOfPatientOrderSchema(props: { isRequired: boolean }) {
  const { isRequired } = props;
  return z
    .object(
      {
        // Don't require consumesType in the input payload, we hardcode it
        sourceBlockName: z.string().min(isRequired ? 1 : 0, {
          message: 'Source block name is required',
        }),
      },
      { message: 'Order is required' }
    )
    .optional()
    .default({
      sourceBlockName: '',
    })
    .transform(({ sourceBlockName }) => ({
      sourceBlockName,
      consumesType: ProducedType.PatientOrder,
    })) as z.ZodType<ConsumerOfPatientOrder, JustSourceBlockName>;
}

export const ConsumerOfFhirPatientResponseSchema = z
  .object({
    sourceBlockName: z.string(),
  })
  .transform(({ sourceBlockName }) => ({
    sourceBlockName,
    consumesType: ProducedType.FhirPatientResponse,
  })) as z.ZodType<ConsumerOfFhirPatientResponse, JustSourceBlockName>;

export const ConsumerOfProviderSchema = z
  .object({
    // Don't require consumesType in the input payload, we hardcode it
    sourceBlockName: z.string(),
  })
  .transform(({ sourceBlockName }) => ({
    sourceBlockName,
    consumesType: ProducedType.Provider,
  })) as z.ZodType<ConsumerOfProvider, JustSourceBlockName>;

export function ConsumerOfVoiceCallSchema(props: { isRequired: boolean }) {
  const { isRequired } = props;
  return z
    .object(
      {
        // Don't require consumesType in the input payload, we hardcode it
        sourceBlockName: z.string().min(isRequired ? 1 : 0, {
          message: 'Source block name is required',
        }),
      },
      { message: 'Voice call is required' }
    )
    .optional()
    .default({
      sourceBlockName: '',
    })
    .transform(({ sourceBlockName }) => ({
      sourceBlockName,
      consumesType: ProducedType.VoiceCall,
    })) as z.ZodType<ConsumerOfVoiceCall, JustSourceBlockName>;
}

export enum DurationUnits {
  Years = 'years',
  Weeks = 'weeks',
  Days = 'days',
  Hours = 'hours',
  Minutes = 'minutes',
  Seconds = 'seconds',
}

export const DurationSchema = z.object({
  value: z.number().min(0),
  units: z.nativeEnum(DurationUnits),
});

export type Duration = z.infer<typeof DurationSchema>;

/**
 * Note - this does not account for DST transitions, or leap years.
 * Regarding DST transitions:
 * 1 day in milliseconds is not the same for a DST transition.
 * 1 day for the switch from standard time to DST is 23 hours.
 * 1 day for the switch from DST to standard time is 25 hours.
 * If you need to account for correct date durations, use date-fns Date math utilities,
 * such as https://date-fns.org/v4.1.0/docs/add
 */
export function getDurationInMillis(value: number, unit: DurationUnits): number {
  const durations: { [key in DurationUnits]: number } = {
    seconds: 1000,
    minutes: 60 * 1000,
    hours: 60 * 60 * 1000,
    days: 24 * 60 * 60 * 1000, // Approximate (Not valid for DST transitions)
    weeks: 7 * 24 * 60 * 60 * 1000, // Approximate (Not valid for DST transitions)
    years: 365 * 24 * 60 * 60 * 1000, // Approximate (Not valid for leap years)
  };
  return value * durations[unit];
}

/**
 * Note - this does not account for DST transitions, or leap years.
 * Regarding DST transitions:
 * 1 day in milliseconds is not the same for a DST transition.
 * 1 day for the switch from standard time to DST is 23 hours.
 * 1 day for the switch from DST to standard time is 25 hours.
 * If you need to account for correct date durations, use date-fns Date math utilities,
 * such as https://date-fns.org/v4.1.0/docs/add
 */
export function convertDurationToMilliseconds(duration: Duration): number {
  return getDurationInMillis(duration.value, duration.units);
}

export const DurationSchemaWithMax = (pathName: string, max: Duration) => {
  const maxMillis = getDurationInMillis(max.value, max.units);
  return DurationSchema.refine(
    (arg: Duration) => {
      const millis = getDurationInMillis(arg.value, arg.units);
      return millis <= maxMillis;
    },
    {
      message: `Max ${pathName} is ${max.value} ${max.units}`,
      path: [pathName],
    }
  );
};

export function StringAsNumberSchema(
  numberValidation: z.ZodType<number | undefined, number | undefined>
) {
  return z.coerce
    .string()
    .optional()
    .transform((value) => (value === '' ? undefined : value))
    .refine((value) => value === undefined || !isNaN(Number(value)), {
      message: 'Invalid number',
    })
    .transform((value) => (value === undefined ? undefined : Number(value)))
    .pipe(numberValidation) as unknown as z.ZodOptional<z.ZodType<number>>;
}
